import type { CellContext, ColumnDef } from '@tanstack/react-table';
import type { TFunction } from 'i18next';
import { Observer } from 'mobx-react-lite';
import type { JSX } from 'react';
import React from 'react';

import type { Account, IAccountUserRole, UserRole } from '@feathr/blackbox';
import type { ITanStackColumnMeta } from '@feathr/components';
import { NumberCell, TanStackHeader, TruncatedTextCell } from '@feathr/components';

import RoleOptions from './RoleOptions';
import RoleTypeChip from './RoleTypeChip';

function countUsersForRole(userRoles: IAccountUserRole[], roleId: string): number {
  return userRoles.filter((userRole): boolean => userRole.role === roleId).length;
}

// Sortable column ids are the fields the list route orders by, so renaming one breaks its sort.
function rolesColumns(t: TFunction, account: Account): Array<ColumnDef<UserRole>> {
  return [
    {
      accessorFn: (row): string => row.name,
      cell: ({ row }: CellContext<UserRole, unknown>): JSX.Element => (
        <Observer>{(): JSX.Element => <TruncatedTextCell value={row.original.name} />}</Observer>
      ),
      header: (): JSX.Element => <TanStackHeader title={t('Role')} />,
      id: 'name',
      meta: { isFlex: true, isPrimary: true } satisfies ITanStackColumnMeta,
    },
    {
      accessorFn: (row): string => row.id,
      cell: ({ row }: CellContext<UserRole, unknown>): JSX.Element => (
        <RoleTypeChip roleId={row.original.id} />
      ),
      header: (): JSX.Element => <TanStackHeader title={t('Type')} />,
      id: 'id',
      meta: { align: 'center' } satisfies ITanStackColumnMeta,
      size: 120,
    },
    {
      cell: ({ row }: CellContext<UserRole, unknown>): JSX.Element => (
        <Observer>
          {(): JSX.Element => (
            <NumberCell value={countUsersForRole(account.get('user_roles', []), row.original.id)} />
          )}
        </Observer>
      ),
      enableSorting: false,
      header: (): JSX.Element => <TanStackHeader title={t('Users Assigned')} />,
      id: 'users-assigned',
      meta: { align: 'right' } satisfies ITanStackColumnMeta,
      size: 152,
    },
    {
      cell: ({ row }: CellContext<UserRole, unknown>): JSX.Element => (
        <RoleOptions role={row.original} />
      ),
      enableSorting: false,
      header: (): JSX.Element => <TanStackHeader title={t('Options')} />,
      id: 'options',
      meta: { align: 'center', pinned: 'right' } satisfies ITanStackColumnMeta,
      size: 80,
    },
  ];
}

export default rolesColumns;
